// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Button from '@mui/material/Button';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import CustomSnackbar from '../ui/Snackbars';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import React from 'react';
import TextField from '@mui/material/TextField';
import useHttp from '../../utils/http';
import SelectVisaDialog from '../visa/SelectVisaDialog.js';
import { adjustForTimezone, getCountryNameFromCountryCode, getDateForDB } from '../../utils/formatting';
import { CircularProgress } from '@mui/material';
import { getTravelStatusColor, getTravelStatusFontColor } from "../../theme/status-colors"
import { NO_VALID_VISA_AVAILABLE_ID, VISA_NOT_APPLICABLE_ID } from '../../Config';
import { postSearchVisas, patchAssignTravelRequest, getTravelRequest } from '../../utils/end-points';
import { travelStatus, travelStatusArray } from "../../utils/const.js"
import { useState, useEffect, useCallback } from 'react';
import "../../css/TravelInfoCard.scss"

const TravelInfoCards = (props) => {

    const [selectedTravel, setSelectedTravel] = useState(null)
    const [travelAgents, setTravelAgents] = useState(props.travelAgents)
    const [selectedTravelAgent, setSelectedTravelAgent] = useState("")
    const [selectedTravelStatus, setSelectedTravelStatus] = useState("")
    const [visa, setVisa] = useState([])
    const [selectedVisa, setSelectedVisa] = useState(null)
    const [travelAgentValidation, setTravelAgentValidation] = useState({ error: false, helperText: "" })
    const [travelStatusValidation, setTravelStatusValidation] = useState({ error: false, helperText: "" })
    const [visaValidation, setVisaValidation] = useState({ error: false, helperText: "" })
    const [confirmationDialogData, setConfirmationDialogData] = useState({ open: false, title: "", content: "", acceptLabel: "", declineLabel: "", acceptAlert: null, declineAlert: null })
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const [visaDialog, setVisaDialog] = useState(false)
    const [isDataLoading, setDataLoading] = useState(true)
    const { handleRequest } = useHttp();

    useEffect(() => {
        fetchEmployeeVisa()
        setSelectedTravel(null)
        setTravelAgents(props.travelAgents)
        setSelectedTravelAgent("")
        setSelectedTravelStatus("")
        setSelectedVisa(null)
        setTravelAgentValidation({ error: false, helperText: "" })
        setTravelStatusValidation({ error: false, helperText: "" })
        setVisaValidation({ error: false, helperText: "" })
    }, [props.jobNumber])

    const fetchEmployeeVisa = useCallback(async () => {
        setDataLoading(true)

        const promises = []

        promises.push(new Promise(function (resolve, reject) {

            let url = postSearchVisas
            let body = {
                email: props.travellerEmail,
                offset: 0,
                status: "active"
            }

            handleRequest(url, "POST", body, (visa) => {
                if (visa) {
                    setVisa(visa)
                    resolve()
                }
                else {
                    setSnackbarData({ message: "Error while fetching visa information", open: true, severity: "error", onClose: onCloseSnackbar })
                    reject()
                }
            }, (error) => {
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                reject()
            })
        }))

        promises.push(new Promise(function (resolve, reject) {

            let url = getTravelRequest.replace("$jobNumber", props.jobNumber)

            handleRequest(url, "GET", null, (travel) => {
                if (travel) {
                    setSelectedTravel(travel)
                    resolve()
                }
                else {
                    setSnackbarData({ message: "Error while fetching visa information", open: true, severity: "error", onClose: onCloseSnackbar })
                    reject()
                }
            }, (error) => {
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                reject()
            })
        }))

        Promise.all(promises)
            .then(() => {
            })
            .catch((error) => {
                console.error(error)
            })
            .finally(()=> {
                setDataLoading(false)
            })


    }, [props.jobNumber])

    const getVisaOptions = () => {
        var visaOptions = []
        var consistValidVisa = false
        var filteredVisa = visa.filter(visa => visa.email === props.travellerEmail)

        var startDate = adjustForTimezone(new Date(props.startDate))
        var endDate = adjustForTimezone(new Date(props.endDate))

        filteredVisa.map(visa => {
            var visaStartDate = adjustForTimezone(new Date(visa.startDate))
            var visaEndDate = adjustForTimezone(new Date(visa.endDate))
            if (visaStartDate <= startDate && visaEndDate >= endDate) {
                if (!consistValidVisa) {
                    visaOptions.pop()
                    consistValidVisa = true
                }
                var obj = { id: visa.id, label: getCountryNameFromCountryCode(visa.country) + " - " + visa.visaType + " - " + visa.entryType + " - (" + getDateForDB(adjustForTimezone(new Date(visa.startDate))) + " to " + getDateForDB(adjustForTimezone(new Date(visa.endDate))) + ")", status: "valid" }
                visaOptions.push(obj)
            }
        })

        visaOptions.push({ id: NO_VALID_VISA_AVAILABLE_ID.id, label: NO_VALID_VISA_AVAILABLE_ID.label })
        visaOptions.push({ id: VISA_NOT_APPLICABLE_ID.id, label: VISA_NOT_APPLICABLE_ID.label })

        // filteredVisa.map(visa => {
        //     var visaStartDate = adjustForTimezone(new Date(visa.startDate))
        //     var visaEndDate = adjustForTimezone(new Date(visa.endDate))

        //     if (!(visaStartDate <= startDate && visaEndDate >= endDate)) {
        //         var obj = { id: visa.id, label: getCountryNameFromCountryCode(visa.country) + " - " + visa.visaType + " - " + visa.entryType + " - (" + getDateForDB(adjustForTimezone(new Date(visa.startDate))) + " to " + getDateForDB(adjustForTimezone(new Date(visa.endDate))) + ")", status: "expired" }
        //         visaOptions.push(obj)
        //     }
        // })


        return visaOptions
    }

    useEffect(() => {
        if (selectedTravelAgent) {
            setTravelAgentValidation({ error: false, helperText: "" })
        }
        if (selectedTravelStatus) {
            setTravelStatusValidation({ error: false, helperText: "" })
        }
        if (selectedVisa) {
            setVisaValidation({ error: false, helperText: "" })
        }
    }, [selectedTravelAgent, selectedTravelStatus, selectedVisa])

    const handleTravelAgentChange = (event) => {
        setSelectedTravelAgent(event.target.value)
        setSelectedTravelStatus(travelStatus.INPROGRESS)
    }

    const handleTravelStatusChange = (event) => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
        setTravelAgentValidation({ error: false, helperText: "" })
        setTravelStatusValidation({ error: false, helperText: "" })
        setVisaValidation({ error: false, helperText: "" })
        setSelectedTravelStatus(event.target.value)
    }

    const resetForm = () => {
        setConfirmationDialogData({
            open: true,
            title: "Form Reset",
            content: "Are you sure you want to reset the from?",
            acceptLabel: "Yes",
            declineLabel: "No",
            acceptAlert: handleAcceptResetAlert,
            declineAlert: handleDeclineAlert
        })
    }

    const handleAcceptResetAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
        setTravelAgentValidation({ error: false, helperText: "" })
        setTravelStatusValidation({ error: false, helperText: "" })
        setVisaValidation({ error: false, helperText: "" })
        setSelectedTravelAgent("")
        setSelectedTravelStatus("")
        setSelectedVisa(null)
    }

    const handleDeclineAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
    }

    const submitForm = () => {

        let formValidated = true;

        if (!selectedTravelStatus) {
            setTravelStatusValidation({ error: true, helperText: "Required" })
            formValidated = false
        }

        // Validating all fields if Travel Status is "INPROGRESS"
        if (selectedTravelStatus === travelStatus.INPROGRESS) {
            if (!selectedTravelAgent) {
                setTravelAgentValidation({ error: true, helperText: "Required" })
                formValidated = false
            }
            if (!selectedVisa) {
                setVisaValidation({ error: true, helperText: "Required" })
                formValidated = false
            }
        }

        if (formValidated) {

            let titleMsg = "Assign Travel Request"
            let contentMsg = "Are you sure you want to assign the travel request?"

            if (selectedTravelStatus === travelStatus.ONHOLD) {
                titleMsg = "Make Travel Request On Hold"
                contentMsg = "Are you sure you want to make this travel request On Hold?"
            }
            else if (selectedTravelStatus === travelStatus.CANCELLED) {
                titleMsg = "Cancel Travel Request"
                contentMsg = "Are you sure you want to Cancel this travel request?"

            }

            setConfirmationDialogData({
                open: true,
                title: titleMsg,
                content: contentMsg,
                acceptLabel: "Yes",
                declineLabel: "No",
                acceptAlert: handleAcceptUpdateAlert,
                declineAlert: handleDeclineAlert
            })
        }

        else {
            setSnackbarData({ message: " Required fields can not be empty. Please check the form again.", open: true, severity: "error", onClose: onCloseSnackbar })
        }

    }

    const handleAcceptUpdateAlert = () => {
        props.setParentLoading(true)
        setConfirmationDialogData(data => ({ ...data, open: false }))
        const body = {
            jobNumber: selectedTravel.jobNumber,
            travelAgentId: travelAgents.filter(agent => agent.nameOfTravelAgent === selectedTravelAgent).length > 0 ? travelAgents.filter(agent => agent.nameOfTravelAgent === selectedTravelAgent)[0].id : null,
            status: selectedTravelStatus,
            visaId: selectedVisa ? selectedVisa.id : null
        }
        updateTravelRequest(body)

    }

    const updateTravelRequest = useCallback(async (body) => {
        handleRequest(patchAssignTravelRequest, "PATCH", body, (data) => {
            if (data) {
                props.submitCallback();
                props.setParentSnackbar("Success", "success")
            }
            else {
                props.setParentSnackbar("Success", "error")
            }
            props.setParentLoading(false)
        }, (error) => {
            props.setParentLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    })

    const handleCloseVisaDialog = (isSubmit, selectedVisa) => {
        setVisaDialog(false);
        if (isSubmit) {
            setSelectedVisa(selectedVisa)
        }
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (
        <React.Fragment>

            <ConfirmationDialog confirmationDialogData={confirmationDialogData} />

            <CustomSnackbar snackbar={snackbarData} />

            <div className='Travel-info-cards'>
                <Paper className='Details-paper Border-elevation'>
                    <div className='Travel-info-title'>Travel Details</div>
                    {isDataLoading ?
                        <CircularProgress className='Secondary-loader' color="secondary" />
                        : <div>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Travel Status : </Grid>
                                {selectedTravel && <Grid item lg={7} className='Travel-info-status' style={{ color: getTravelStatusColor(selectedTravel.status) }}>
                                    <span className='Status-chip'
                                        style={{
                                            backgroundColor: getTravelStatusColor(selectedTravel.status),
                                            color: getTravelStatusFontColor(selectedTravel.status)
                                        }}>
                                        {selectedTravel.status}
                                    </span>
                                </Grid>}
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Job Number : </Grid>
                                <Grid item lg={7}>{selectedTravel?.jobNumber}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Traveller's Email : </Grid>
                                <Grid item lg={7}>{selectedTravel?.travellerEmail}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Engagement ID : </Grid>
                                <Grid item lg={7}>{selectedTravel?.engagementId || "-"}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Customer : </Grid>
                                <Grid item lg={7}>{selectedTravel?.customerName}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Country : </Grid>
                                <Grid item lg={7}>{selectedTravel?.country}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Travel Location : </Grid>
                                <Grid item lg={7}>{selectedTravel?.city}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Base Location : </Grid>
                                <Grid item lg={7}>{selectedTravel?.employeeLocation}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>Start Date : </Grid>
                                <Grid item lg={7}>{getDateForDB(adjustForTimezone(new Date(selectedTravel?.startDate)))}</Grid>
                            </Grid>
                            <Grid container className='Travel-info-entry'>
                                <Grid item lg={5} className='Travel-info-span'>End Date : </Grid>
                                <Grid item lg={7}>{getDateForDB(adjustForTimezone(new Date(selectedTravel?.endDate)))}</Grid>
                            </Grid>
                        </div>
                    }
                </Paper>

                <Paper className='Operations-paper Border-elevation'>
                    <div className='Travel-info-title'>Travel Operations</div>
                    <div className='Travel-input-entry'>
                        <FormControl fullWidth size="small"
                            error={travelAgentValidation.error}>
                            <InputLabel>Travel Handled By</InputLabel>
                            <Select value={selectedTravelAgent} label="Travel Handled By" onChange={handleTravelAgentChange} required>
                                {travelAgents && travelAgents.length > 0 && travelAgents.map(agent => {
                                    return <MenuItem key={agent.id} value={agent.nameOfTravelAgent}>{agent.nameOfTravelAgent}</MenuItem>
                                })}
                            </Select>
                        </FormControl>
                    </div>
                    <div className='Travel-input-entry'>
                        <FormControl fullWidth size="small"
                            error={travelStatusValidation.error} >
                            <InputLabel>Travel Status</InputLabel>
                            <Select value={selectedTravelStatus} label="Travel Status" onChange={handleTravelStatusChange}
                                required>
                                {/* TODO */}
                                {travelStatusArray.length > 0 && travelStatusArray.filter(status => (status != travelStatus.NEW & status != travelStatus.COMPLETED & status != travelStatus.EXTENDED)).map(status => {
                                    // {travelStatusArray.length > 0 && travelStatusArray.map(status => {
                                    return <MenuItem key={status} value={status}>
                                        <span className="Status-dot-dropdown" style={{ backgroundColor: getTravelStatusColor(status) }}></span>{status}
                                    </MenuItem>
                                })}
                            </Select>
                        </FormControl>
                    </div>
                    <div className='Travel-input-entry'>
                        <TextField size="small" label="Select Visa" variant="outlined" fullWidth onClick={() => setVisaDialog(true)}
                            error={visaValidation.error} value={selectedVisa ? selectedVisa.label : ""} />
                    </div>
                    <div className='Travel-operation-buttons'>
                        <Button variant="contained" size="small" color="secondary" className='Submit-button' onClick={submitForm}>Submit</Button>
                        <Button variant="outlined" size="small" className='Reset-button' onClick={resetForm}>Reset</Button>
                    </div>
                </Paper>
            </div>

            <SelectVisaDialog
                open={visaDialog}
                onClose={handleCloseVisaDialog}
                selectedVisaValue={selectedVisa}
                email={props.travellerEmail}
                options={getVisaOptions()}
                isDataLoading={isDataLoading} />

        </React.Fragment>
    );
}

export default TravelInfoCards;